import { useCallback, useEffect, useRef, useState } from 'react';
import { accountScope } from '@heraion/shared';

import { useRetryOnReconnect } from '../useRetryOnReconnect';
import type { ResourceCache } from './state/asyncListCache';

// Single-resource SWR hook, the twin of useAsyncList for an endpoint that
// returns one object, not a paginated list. THE copy for both apps since #397
// (mobile had its own until then). The error is `unknown`: each app owns its
// ApiError class, and a caller that needs the code reads `message`.
//
// Semantics:
//   - Mount: read cache, render if present, fire fetch in background.
//   - Refresh: re-fetch, replace state, persist cache.
//   - Abort: every refresh allocates a fresh AbortController; a
//     re-entry cancels the in-flight call (most-recent wins).
//   - Reconnect (#397): a resource left in error reads again.

type AsyncResourceStatus =
  | 'idle'
  | 'loading'
  | 'loaded'
  | 'refreshing'
  | 'error';

type Options<T> = {
  /** Stable identifier, used as the effect dependency: changing it (a scope
   *  toggle) re-hydrates from the new cache and re-fetches. */
  key: string;
  fetch: (signal?: AbortSignal) => Promise<T>;
  cache?: ResourceCache<T>;
  /** Run on mount. Default true. */
  immediate?: boolean;
};

type AsyncResourceState<T> = {
  value: T | null;
  status: AsyncResourceStatus;
  error: unknown;
  refresh: () => Promise<void>;
};

export function useAsyncResource<T>(opts: Options<T>): AsyncResourceState<T> {
  const { key, fetch, cache, immediate = true } = opts;
  const [value, setValue] = useState<T | null>(null);
  const [status, setStatus] = useState<AsyncResourceStatus>('idle');
  const [error, setError] = useState<unknown>(null);
  const abortRef = useRef<AbortController | null>(null);
  const fetchRef = useRef(fetch);
  const cacheRef = useRef(cache);
  useEffect(() => {
    fetchRef.current = fetch;
    cacheRef.current = cache;
  }, [fetch, cache]);

  const refresh = useCallback(async (): Promise<void> => {
    abortRef.current?.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    setStatus((prev) => (prev === 'loaded' ? 'refreshing' : 'loading'));
    setError(null);
    // #398: the account this read is for; the copy is written under it, so a
    // refresh with no cache read before it (`immediate: false`) still writes.
    const scope = accountScope();
    try {
      const fresh = await fetchRef.current(ctrl.signal);
      if (ctrl.signal.aborted) return;
      setValue(fresh);
      setStatus('loaded');
      if (cacheRef.current) {
        void cacheRef.current.write(fresh, scope);
      }
    } catch (e) {
      if (ctrl.signal.aborted) return;
      setStatus('error');
      setError(e);
    }
  }, []);

  useEffect(() => {
    // A new key must not show the old key's value.
    setValue(null);
    // Nothing is fetched without `immediate`: it must not wait on a read that never starts.
    setStatus(immediate ? 'loading' : 'idle');
    if (!immediate) return;
    let cancelled = false;
    void (async () => {
      if (cacheRef.current) {
        const cached = await cacheRef.current.read();
        if (cancelled) return;
        if (cached !== null) {
          setValue(cached);
          setStatus('loaded');
        }
      }
      if (cancelled) return;
      void refresh();
    })();
    return () => {
      cancelled = true;
      abortRef.current?.abort();
    };
  }, [key, immediate, refresh]);

  const retry = useRetryOnReconnect(status === 'error');
  useEffect(() => {
    if (retry > 0) void refresh();
  }, [retry, refresh]);

  return { value, status, error, refresh };
}
